import React, { useEffect, useState } from "react";
import { X, Plus, ListTree, Trash2, Check } from "lucide-react";
import clsx from "clsx";
import { toast } from "sonner";

import { tasksApi, blocksApi } from "@/lib/api";
import AiHelpers from "./AiHelpers";
import RecurrencePicker from "./RecurrencePicker";

/**
 * Minimal new-task creation flow.
 *
 *  Stage 1 (always visible): title + room (required), default duration = 60 min.
 *  Stage 2 (optional): add subtasks — if any exist, the parent duration becomes the
 *    SUM of all subtask durations (and the direct duration input disables).
 *
 *  On submit:
 *    1. POST /api/tasks (parent) with primary_room_id + duration_min (computed if subs).
 *    2. POST /api/tasks for each subtask (parent_task_id = step 1's id).
 *    3. POST /api/projects/{pid}/schedule-blocks scheduling the parent on the chosen day,
 *       starting at the day's first free slot (or 09:00 if empty).
 */

const DEFAULT_DAY_START = "09:00"; // venue-local; could be project setting later
const DEFAULT_DURATION = 60;

function isoDateForDay(dateKey, hhmm) {
  // dateKey like "2026-06-12"; hhmm "09:00" → "2026-06-12T09:00:00Z" (treat as local-naive)
  return `${dateKey}T${hhmm}:00Z`.replace("Z", "+00:00").replace("+00:00", "Z");
}

function addMinutesIso(iso, minutes) {
  return new Date(new Date(iso).getTime() + minutes * 60000).toISOString().replace(/\.\d+Z$/, "Z");
}

function sumDuration(subs) {
  return subs.reduce((s, st) => s + (parseInt(st.duration_min, 10) || 0), 0);
}

function findDayStart(blocks, dateKey) {
  // Find the latest end_at on that day; return ISO string (UTC) for new task's start.
  // Tolerant of mixed string formats.
  const ondate = blocks.filter((b) => {
    const d = (b.start_at || "").slice(0, 10);
    return d === dateKey;
  });
  if (!ondate.length) return isoDateForDay(dateKey, DEFAULT_DAY_START);
  const latest = ondate.reduce((m, b) => (new Date(b.end_at) > new Date(m) ? b.end_at : m), ondate[0].end_at);
  return latest.replace(/\.\d+Z$/, "Z");
}

export default function NewTaskDialog({ open, dateKey, projectId, rooms, blocks, floating, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [roomId, setRoomId] = useState("");
  const [durationMin, setDurationMin] = useState(DEFAULT_DURATION);
  const [subs, setSubs] = useState([]); // {_id, title, duration_min}
  const [recurrenceRule, setRecurrenceRule] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle("");
      setRoomId(rooms[0]?.id || "");
      setDurationMin(DEFAULT_DURATION);
      setSubs([]);
      setRecurrenceRule(null);
    }
  }, [open, rooms]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, busy]);

  if (!open) return null;

  const hasSubs = subs.length > 0;
  const effectiveDuration = hasSubs ? sumDuration(subs) : (parseInt(durationMin, 10) || 0);

  const addSub = () => setSubs((arr) => [...arr, { _id: Date.now() + Math.random(), title: "", duration_min: 30 }]);
  const removeSub = (id) => setSubs((arr) => arr.filter((s) => s._id !== id));
  const patchSub = (id, patch) => setSubs((arr) => arr.map((s) => (s._id === id ? { ...s, ...patch } : s)));

  const submit = async () => {
    if (busy) return;
    const cleanTitle = title.trim();
    if (!cleanTitle) {
      toast.error("Title is required.");
      return;
    }
    if (!floating && !roomId) {
      toast.error("Room is required.");
      return;
    }
    if (hasSubs && subs.some((s) => !(s.title || "").trim())) {
      toast.error("All subtasks need a title.");
      return;
    }
    if (effectiveDuration < 15) {
      toast.error("Duration must be at least 15 minutes.");
      return;
    }
    setBusy(true);
    try {
      // 1) Parent task
      const parent = await tasksApi.create(projectId, {
        title: cleanTitle,
        primary_room_id: roomId || null,
        duration_min: effectiveDuration,
        priority: "normal",
        is_floating: !!floating,
        is_recurring: !!(recurrenceRule && !floating),
        recurrence_rule: !floating ? recurrenceRule : null,
      });
      // 2) Subtasks
      for (const s of subs) {
        await tasksApi.create(projectId, {
          title: s.title.trim(),
          parent_task_id: parent.id,
          duration_min: parseInt(s.duration_min, 10) || 30,
          primary_room_id: roomId || null,
        });
      }
      // 3) Schedule block on that day (only for date-anchored flow; floating skips this)
      if (!floating && dateKey) {
        const startAt = findDayStart(blocks, dateKey);
        const endAt = addMinutesIso(startAt, effectiveDuration);
        await blocksApi.create(projectId, {
          task_id: parent.id,
          room_id: roomId,
          start_at: startAt,
          end_at: endAt,
          is_locked: false,
        });
        // If recurrence set, materialise immediately so the producer sees all instances.
        if (recurrenceRule) {
          try {
            const r = await tasksApi.materialize(parent.id);
            if (r?.created_count) {
              toast.success(`Materialised ${r.created_count} more occurrence${r.created_count === 1 ? "" : "s"}`);
            }
          } catch (e) {
            // Non-fatal — task + first block were created OK.
            console.warn("Materialise failed:", e?.response?.data?.detail || e);
          }
        }
      }
      toast.success(
        floating
          ? `Added "${cleanTitle}" to floating tray.`
          : `Created "${cleanTitle}"${hasSubs ? ` + ${subs.length} subtask${subs.length === 1 ? "" : "s"}` : ""}.`
      );
      await onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create task.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6"
      onClick={busy ? undefined : onClose}
      data-testid="new-task-overlay"
    >
      <div
        className="w-[520px] max-w-full max-h-[88vh] bg-bg-surface border border-line-strong shadow-[0_24px_64px_rgba(0,0,0,0.8)] animate-slide-up flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-task-dialog"
      >
        <div className="flex items-center justify-between p-3 border-b border-line-strong bg-bg-elevated">
          <div>
            <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">
              {floating ? "NEW FLOATING TASK" : "NEW TASK"}
            </div>
            <div className="text-sm text-ink mt-0.5">
              {floating ? "Not placed on schedule yet" : dateKey}
            </div>
          </div>
          <button onClick={onClose} disabled={busy} className="btn-ghost" data-testid="new-task-close">
            <X className="w-3 h-3" />
          </button>
        </div>

        <div className="p-4 space-y-3 flex-1 overflow-auto">
          <Field label="TITLE *">
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !hasSubs) submit(); }}
              placeholder="What needs to happen?"
              className="w-full bg-bg-elevated text-sm p-2 outline-none border border-line focus:border-amber"
              data-testid="new-task-title"
            />
          </Field>

          <Field label={floating ? "PRIMARY ROOM (optional)" : "ROOM *"}>
            <select
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              className="w-full bg-bg-elevated text-sm p-2 outline-none border border-line focus:border-amber"
              data-testid="new-task-room"
            >
              {floating && <option value="">— unassigned —</option>}
              {!floating && rooms.length === 0 && <option value="">— no rooms in this project —</option>}
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>

          <Field
            label={hasSubs ? `DURATION · ${effectiveDuration}m (auto from subtasks)` : "DURATION"}
          >
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="15"
                step="15"
                value={hasSubs ? effectiveDuration : durationMin}
                disabled={hasSubs}
                onChange={(e) => setDurationMin(e.target.value === "" ? "" : parseInt(e.target.value, 10))}
                className={clsx(
                  "flex-1 mono tabular-nums bg-bg-elevated text-sm p-2 outline-none border focus:border-amber",
                  hasSubs ? "border-line text-ink-mute" : "border-line"
                )}
                data-testid="new-task-duration"
              />
              <span className="mono text-2xs text-ink-mute uppercase tracking-widest">MIN</span>
            </div>
          </Field>

          <AiHelpers
            projectId={projectId}
            title={title}
            roomId={roomId}
            parentDurationMin={hasSubs ? effectiveDuration : (parseInt(durationMin, 10) || 0)}
            onEstimateApplied={(est) => setDurationMin(est.likely_minutes)}
            onSubtaskAdded={(s) =>
              setSubs((arr) => [...arr, { _id: Date.now() + Math.random(), title: s.title, duration_min: s.duration_min }])
            }
          />

          {!floating && (
            <RecurrencePicker value={recurrenceRule} onChange={setRecurrenceRule} disabled={busy} />
          )}

          {/* Subtasks */}
          <div>
            <div className="mono text-2xs text-ink-mute uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
              <ListTree className="w-3 h-3" /> SUBTASKS {subs.length > 0 && `· ${subs.length}`}
            </div>
            <div className="space-y-1">
              {subs.map((s) => (
                <div key={s._id} className="flex items-center gap-2" data-testid={`sub-row-${s._id}`}>
                  <input
                    value={s.title}
                    onChange={(e) => patchSub(s._id, { title: e.target.value })}
                    placeholder="Subtask title"
                    className="flex-1 bg-bg-elevated text-xs p-1.5 outline-none border border-line focus:border-amber"
                  />
                  <input
                    type="number"
                    min="15"
                    step="15"
                    value={s.duration_min}
                    onChange={(e) => patchSub(s._id, { duration_min: parseInt(e.target.value, 10) || 0 })}
                    className="w-20 mono tabular-nums bg-bg-elevated text-xs p-1.5 outline-none border border-line focus:border-amber"
                  />
                  <span className="mono text-2xs text-ink-mute uppercase">MIN</span>
                  <button
                    onClick={() => removeSub(s._id)}
                    className="btn-ghost text-ink-mute hover:text-red shrink-0"
                    title="Remove"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button
                onClick={addSub}
                className="btn-ghost w-full justify-center"
                data-testid="new-task-add-sub"
              >
                <Plus className="w-3 h-3" /> ADD SUBTASK
              </button>
            </div>
            {hasSubs && (
              <p className="mono text-2xs text-ink-mute uppercase tracking-widest mt-2">
                Parent duration equals the sum of subtasks.
              </p>
            )}
          </div>
        </div>

        <div className="p-3 border-t border-line bg-bg-surface flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="btn-ghost" data-testid="new-task-cancel">
            CANCEL
          </button>
          <button
            onClick={submit}
            disabled={busy || !title.trim() || (!floating && !roomId)}
            className="btn-primary px-4"
            data-testid="new-task-submit"
          >
            <Check className="w-3 h-3" />
            {busy ? "Creating…" : (floating ? "Add to floating" : "Create task")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="mono text-2xs text-ink-mute uppercase tracking-widest mb-1">{label}</div>
      {children}
    </div>
  );
}
